import { useRef, useState } from 'react';

import {
  resendFamilyEventMail,
  resendFamilyEventSms,
  updateFamilyEventDetails,
} from '@src/api/event';
import {
  GetFamilyEventDetailsOfSmsQuery,
  GetFamilyEventDetailsOfSmsResponse,
} from '@src/api/event/getFamilyEventDetailsOfSms';
import TableHeader from '@src/components/TableHeader';
import Text from '@src/components/Text';
import { useGetFamilyEventDetailsOfMail } from '@src/hooks/queries/event/useGetFamilyEventDetailsOfMail';
import useGetFamilyEventDetailsOfSms from '@src/hooks/queries/event/useGetFamilyEventDetailsOfSms';
import { Button, Divider, Modal, Table, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';

import { closeModal } from '@reduce/modals';

import type * as T from './type';

const PAGE_SIZE: number = 8;

function FamilyEventDetailsModal({ familyEventId }: T.FamilyEventDetailsModalProps) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const close = () => dispatch(closeModal('familyEventDetailsModal'));

  const { data: mailDetails, isLoading: isMailLoading } = useGetFamilyEventDetailsOfMail({
    eventId: familyEventId,
  });

  const [page, setPage] = useState<number>(1);
  const [dir, setDir] = useState<GetFamilyEventDetailsOfSmsQuery['dir']>('desc');
  const [searchValue, setSearchValue] = useState<string>('');
  const {
    data: dataSource,
    total,
    isLoading,
    refetch,
  } = useGetFamilyEventDetailsOfSms({
    page,
    dir,
    limit: PAGE_SIZE,
    q: searchValue || undefined,
    eventId: familyEventId,
  });

  const onSearch = (value: string) => {
    if (value && value.length < 2) {
      message.info('검색어는 2글자 이상이어야 합니다.');
      return false;
    }

    setPage(1);
    setSearchValue(value);
  };

  const [isResendLoading, setIsResendLoading] = useState(false);
  const resendLoadingRef = useRef(false);
  const onResend = async (type: 'sms' | 'mail', phone?: string, name?: string) => {
    if (type === 'sms') {
      if (phone && name) {
        await resendFamilyEventSms({
          path: { eventId: familyEventId },
          data: { phone, name },
        });
        queryClient.invalidateQueries(['FAMILY_EVENTS_DETAILS_OF_SMS']);
      } else {
        message.error('핸드폰 번호, 이름이 없으면 재발송 할 수 없습니다.');
      }
    } else {
      await resendFamilyEventMail({ path: { eventId: familyEventId } });
      queryClient.invalidateQueries(['FAMILY_EVENTS_DETAILS_OF_MAIL']);
    }
  };

  const smsRefresh = async () => {
    await updateFamilyEventDetails({ path: { eventId: familyEventId } });
    queryClient.invalidateQueries(['FAMILY_EVENTS_DETAILS_OF_SMS']);
  };

  const columns = (type: 'sms' | 'mail') =>
    [
      {
        title: '발송일시',
        dataIndex: 'createdAt',
        width: '15%',
        render: createdAt => dayjs(createdAt).format('YYYY.MM.DD HH.mm.ss'),
      },
      ...(type === 'sms'
        ? [
            {
              title: '총 발송 건수',
              dataIndex: 'mesg',
              width: '8%',
            },
          ]
        : []),
      {
        title: '결과',
        dataIndex: 'resultStatus',
        width: '15%',
        render: (resultStatus: boolean | null) =>
          resultStatus === null ? '전송완료' : resultStatus ? '성공' : '실패',
      },
      {
        title: '',
        key: 'action',
        width: '15%',
        render: (_, { phone, shipmentType, name }) => {
          if (type === 'sms') return null;

          return (
            <Text
              underline={true}
              cursor={isResendLoading ? undefined : 'pointer'}
              onClick={() =>
                setShowConfirmModal({ phone, name, shipmentType: shipmentType as 'sms' | 'mail' })
              }>
              {shipmentType === 'sms' ? '문자' : '메일'} 재전송
            </Text>
          );
        },
      },
    ] as ColumnsType<GetFamilyEventDetailsOfSmsResponse['rows'][number]>;

  const [showConfirmModal, setShowConfirmModal] = useState<
    | {
        shipmentType: 'sms' | 'mail';
        phone: string | null;
        name: string | null;
      }
    | undefined
  >();

  const reSend = async ({
    name,
    phone,
    shipmentType,
  }: {
    shipmentType: 'sms' | 'mail';
    phone: string | null;
    name: string | null;
  }) => {
    if (isResendLoading || resendLoadingRef.current) return;
    setIsResendLoading(true);
    resendLoadingRef.current = true;
    await onResend(shipmentType, phone || undefined, name || undefined);
    setIsResendLoading(false);
    resendLoadingRef.current = false;
  };

  return (
    <>
      <Modal
        centered
        open={!!showConfirmModal}
        width='440px'
        closable={false}
        footer={[
          <Button key='cancel' loading={isLoading} onClick={() => setShowConfirmModal(undefined)}>
            아니요
          </Button>,
          <Button
            key='reSend'
            type='primary'
            loading={isLoading}
            onClick={() => {
              if (showConfirmModal) reSend(showConfirmModal);
              setShowConfirmModal(undefined);
            }}>
            재전송
          </Button>,
        ]}>
        <div style={{ display: 'flex', height: '100%', alignItems: 'center' }}>
          <p>정말 재전송 하시겠습니끼?</p>
        </div>
      </Modal>
      <Modal
        open={true}
        title='발송 현황'
        footer={null}
        width='1200px'
        bodyStyle={{ paddingTop: '12px' }}
        onCancel={close}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 'bold', margin: '8px 0px' }}>메일 발송 현황</div>
          <Table
            columns={columns('mail')}
            dataSource={mailDetails}
            loading={isMailLoading}
            sticky={true}
            scroll={{ y: 450 }}
            pagination={false}
          />
          <Divider />
          <div style={{ fontSize: 18, fontWeight: 'bold' }}>문자 발송 현황</div>
          <TableHeader
            searchInput={true}
            rightButtons={[{ text: '새로고침', buttonProps: { onClick: smsRefresh } }]}
            inputProps={{
              onSearch,
              placeholder: '이름 또는 연락처 검색',
              style: { order: '1', width: '100%', marginLeft: '0', marginRight: '16px' },
            }}
          />
          <Table
            columns={columns('sms')}
            dataSource={dataSource}
            loading={isLoading}
            pagination={{
              total,
              position: ['bottomCenter'],
              pageSize: PAGE_SIZE,
              showSizeChanger: false,
              onChange: setPage,
            }}
            sticky={true}
            scroll={{ y: 450 }}
          />
        </div>
      </Modal>
    </>
  );
}

export default FamilyEventDetailsModal;
